import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { AlertCircle, ClipboardList, RefreshCw } from 'lucide-react';
import { itsmQueueApi, type MyQueueItem, type ResolverQueue } from '../services/api';
import { PriorityPill, SlaBadge } from '../components/itsm/PrioritySla';
import { relTime, useSlaPolicy } from '../components/itsm/itsmSla';

/**
 * My queue (EP-27 FE-4, PO46-1287 / PO46-1288): the open tickets whose resolver queue is
 * a user group I belong to, earliest SLA due first. Membership is read by the server on
 * every load, so joining or leaving a group changes this list with nothing to re-stamp.
 *
 * Built to the HS design standard (v1.1): slate ramp and status tokens only (no gray-*, no
 * raw hex) on every surface in light and dark; status carries a word or icon as well as a
 * colour (SlaBadge, PriorityPill); the queue filter's label is bound to it; visible focus on
 * every control; a skeleton while loading, a plain-words error with a retry, and no
 * personal data beyond the assignee a resolver needs.
 */

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-950';
const muted = 'text-slate-600 dark:text-slate-400';
const fieldCls = 'px-2 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100';

const TYPE_LABEL: Record<string, string> = {
  incident: 'Incident', request: 'Request', problem: 'Problem',
  change: 'Change', event: 'Event', opstask: 'Ops task',
};

export default function MyQueuePage() {
  const filterId = useId();
  const pol = useSlaPolicy();
  const warnAt = pol?.warnAt ?? 0.75;
  const [queues, setQueues] = useState<(ResolverQueue & { open: number })[]>([]);
  const [items, setItems] = useState<MyQueueItem[]>([]);
  const [groupId, setGroupId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await itsmQueueApi.mine();
      setQueues(data.queues || []);
      setItems(data.items || []);
      setLoadedAt(Date.now());
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      setError(`Your queue could not be loaded${msg ? `: ${msg}` : ''}. Try again; if it keeps failing, the service desk API may be down.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = groupId ? items.filter((i) => i.queueGroupId === groupId) : items;
  const queueName = (id: string) => queues.find((q) => q.id === id)?.name || id;

  return (
    <div className="flex-1 min-w-0 p-6 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <header className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ClipboardList size={20} className="text-accent" aria-hidden="true" />
            My queue
          </h1>
          <p className={clsx('text-sm mt-1', muted)}>
            Open tickets in the queues you belong to, most urgent SLA first. You are told by
            email and Chat when one lands here.
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}
          className={clsx('flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white hover:bg-accent-hover disabled:opacity-60', focusRing)}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {queues.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label htmlFor={filterId} className="text-xs font-medium text-slate-700 dark:text-slate-300">
            Queue
          </label>
          <select id={filterId} value={groupId} onChange={(e) => setGroupId(e.target.value)}
            className={clsx(fieldCls, focusRing)}>
            <option value="">All my queues ({items.length})</option>
            {queues.map((q) => <option key={q.id} value={q.id}>{q.name} ({q.open})</option>)}
          </select>
          {loadedAt && (
            <span className={clsx('text-xs ml-auto', muted)} aria-live="polite">
              {shown.length} open · updated {new Date(loadedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      {error && (
        <div role="alert"
          className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {loading && !loadedAt ? (
        <div aria-busy="true" aria-label="Loading your queue" className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse motion-reduce:animate-none" />
          ))}
        </div>
      ) : !error && queues.length === 0 ? (
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-8 text-center">
          <ClipboardList size={36} className="mx-auto mb-3 text-slate-400 dark:text-slate-500" aria-hidden="true" />
          <p className="font-medium">You are not in any queue</p>
          <p className={clsx('text-sm mt-1 max-w-md mx-auto', muted)}>
            A queue is a user group that owns assets in the CMDB. Ask an administrator to add you
            to your team's group, and its tickets will appear here.
          </p>
        </div>
      ) : !error && shown.length === 0 ? (
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-8 text-center">
          <p className="font-medium">Nothing waiting</p>
          <p className={clsx('text-sm mt-1', muted)}>No open tickets in {groupId ? queueName(groupId) : 'your queues'}.</p>
        </div>
      ) : shown.length > 0 ? (
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Open tickets in my queues, most urgent SLA first</caption>
            <thead className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
              <tr>
                <th scope="col" className="text-left font-medium px-3 py-2">Ticket</th>
                <th scope="col" className="text-left font-medium px-3 py-2">Priority</th>
                <th scope="col" className="text-left font-medium px-3 py-2">SLA</th>
                <th scope="col" className="text-left font-medium px-3 py-2">Status</th>
                <th scope="col" className="text-left font-medium px-3 py-2">Queue</th>
                <th scope="col" className="text-left font-medium px-3 py-2">Assignee</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t._id} className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900">
                  <td className="px-3 py-2">
                    <Link to={`/item/${encodeURIComponent(t._id)}`}
                      className={clsx('rounded hover:text-accent', focusRing)}>
                      <span className={clsx('font-mono text-xs mr-2', muted)}>{t.key || t._id}</span>
                      {t.title}
                    </Link>
                    <span className={clsx('ml-2 text-xs', muted)}>{TYPE_LABEL[t.workItemType] || t.workItemType}</span>
                  </td>
                  <td className="px-3 py-2"><PriorityPill priority={t.priority} /></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <SlaBadge item={t} warnAt={warnAt} />
                      {t.slaNextDue
                        ? <span className={clsx('text-xs', muted)} title={t.slaNextDue}>due {relTime(t.slaNextDue)}</span>
                        : <span className={clsx('text-xs', muted)}>no clock</span>}
                    </span>
                  </td>
                  <td className={clsx('px-3 py-2', muted)}>{t.status}</td>
                  <td className={clsx('px-3 py-2', muted)}>{queueName(t.queueGroupId)}</td>
                  <td className={clsx('px-3 py-2', muted)}>{t.assignee || 'Unassigned'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
